/**
 * API клиент для работы с бэкендом
 * Адаптированная версия для React приложения
 */

class APIClient {

    /**
     * Базовый метод для выполнения запросов
     */
    async request(endpoint, options = {}) {
        const url = endpoint; // Используем endpoint как есть из модели
        const config = {
            ...options,
            headers: {
                ...options.headers
            }
        };

        // Если есть тело запроса и нет явного Content-Type
        if (options.body && typeof options.body === 'object') {
            if (!config.headers['Content-Type']) {
                config.headers['Content-Type'] = 'application/json';
            }
            config.body = JSON.stringify(options.body);
        }
        
        // Добавляем credentials если не указано явно
        if (!('credentials' in config)) {
            config.credentials = 'include';
        }

        try {
            const response = await fetch(url, config);
            
            if (!response.ok) {
                // Пытаемся получить сообщение об ошибке из ответа
                let errorMessage = `HTTP ${response.status}`;
                let errorData = null;
                
                try {
                    const contentType = response.headers.get('content-type');
                    if (contentType && contentType.includes('application/json')) {
                        errorData = await response.json();
                        errorMessage = errorData.message || errorData.error || errorMessage;
                    } else {
                        // Если пришел не JSON, читаем как текст
                        const text = await response.text();
                        console.warn('Non-JSON response:', text.substring(0, 100));
                        errorMessage = `Server returned non-JSON response (${response.status})`;
                    }
                } catch (e) {
                    console.error('Error parsing response:', e);
                    errorMessage = `HTTP ${response.status}`;
                }
                
                // Создаем ошибку с дополнительными данными
                const error = new Error(errorMessage);
                error.status = response.status;
                error.data = errorData;
                throw error;
            }
            
            // Парсим JSON ответ
            const data = await response.json();
            return data;
            
        } catch (error) {
            // Если это сетевая ошибка
            if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
                error.isNetworkError = true;
            }
            
            throw error;
        }
    }

    /**
     * GET запрос
     */
    async get(endpoint, params = {}) {
        const queryString = new URLSearchParams(params).toString();
        const url = queryString ? `${endpoint}?${queryString}` : endpoint;
        
        return this.request(url, {
            method: 'GET'
        });
    }

    /**
     * POST запрос
     */
    async post(endpoint, data = {}) {
        return this.request(endpoint, {
            method: 'POST',
            body: data
        });
    }

    /**
     * PUT запрос
     */
    async put(endpoint, data = {}) {
        return this.request(endpoint, {
            method: 'PUT',
            body: data
        });
    }

    /**
     * DELETE запрос
     */
    async delete(endpoint) {
        return this.request(endpoint, {
            method: 'DELETE'
        });
    }

    /**
     * PATCH запрос
     */
    async patch(endpoint, data = {}) {
        return this.request(endpoint, {
            method: 'PATCH',
            body: data
        });
    }

    /**
     * Загрузка файла
     */
    async upload(endpoint, fileOrData, options = {}) {
        const formData = new FormData();
        
        // Если передан файл напрямую
        if (fileOrData instanceof File || fileOrData instanceof Blob) {
            const fieldName = options.fieldName || 'file';
            formData.append(fieldName, fileOrData);
        } 
        // Если передан объект с файлами и данными
        else if (typeof fileOrData === 'object') {
            Object.entries(fileOrData).forEach(([key, value]) => {
                if (value instanceof File || value instanceof Blob) {
                    formData.append(key, value);
                } else if (Array.isArray(value)) {
                    value.forEach(item => formData.append(key, item));
                } else {
                    formData.append(key, value);
                }
            });
        }
        
        // Добавляем дополнительные данные если есть
        if (options.additionalData) {
            Object.entries(options.additionalData).forEach(([key, value]) => {
                formData.append(key, value);
            });
        }
        
        const response = await fetch(endpoint, {
            method: options.method || 'POST',
            body: formData,
            credentials: options.credentials || 'include',
            ...options.fetchOptions
        });
        
        if (!response.ok) {
            let errorMessage = response.statusText;
            try {
                const errorData = await response.json();
                errorMessage = errorData.message || errorMessage;
            } catch (e) {
                // Используем statusText если не можем распарсить JSON
            }
            
            const error = new Error(errorMessage);
            error.status = response.status;
            throw error;
        }
        
        return response.json();
    }
}

// Создаем singleton экземпляр
const apiClient = new APIClient();

export default apiClient;